import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { tap, map, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { RouterActions } from '@actions/index';

@Injectable()
export class RouterEffects {
  navigateToRoute(route: String, queryParams: { [key: string]: string } = {}) {
    this.router.navigate([route], { queryParams });

    let searchStr = new URLSearchParams(queryParams).toString()
    if (searchStr.length > 0) {
      searchStr = `?${searchStr}`
    }

    this.store.dispatch(RouterActions.changeCurrentRoute({
      newCurrentRoute: route + searchStr
    }));
  }

  navigateAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateAuth),
        tap(() => this.navigateToRoute('/auth'))
      ),
    { dispatch: false }
  );

  navigateSignupProcess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateSignUpProcess),
        tap(() => this.navigateToRoute('/auth/signup-process'))
      ),
    { dispatch: false }
  );

  navigatePasswordRecover$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigatePasswordRecover),
        tap(() => this.navigateToRoute('auth/recovery-pass'))
      ),
    { dispatch: false }
  );

  navigateChangePassword$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateChangePassword),
        tap(() => this.navigateToRoute('change-password'))
      ),
    { dispatch: false }
  );

  navigateChangePasswordConfirmed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateChangePasswordConfirmed),
        tap(() => this.navigateToRoute('business/business-profile/location-profile'))
      ),
    { dispatch: false }
  );

  navigateContacts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateContacts),
        map(param => param.urlParam),
        tap((param) => this.router.navigate(['/contacts'], { queryParams: { user: param } }))
      ),
    { dispatch: false }
  );

  navigateUsers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateUsers),
        map(param => param.urlParam),
        tap((param) => this.router.navigate(['/users'], { queryParams: { user: param } }))
      ),
    { dispatch: false }
  );

  navigateBusinessDashboard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateBusinessDashboard),
        tap(() => this.navigateToRoute('admin-business/dashboard'))
      ),
    { dispatch: false }
  );

  navigateLocationDashboard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateLocationDashboard),
        tap(() => this.navigateToRoute('admin-location/dashboard'))
      ),
    { dispatch: false }
  );

  navigateReviewTargets$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateReviewTargets),
        tap(() => this.navigateToRoute('/review-targets/list-rt'))
      ),
    { dispatch: false }
  );

  navigateReviewTargetsLocation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateReviewTargetsLocation),
        tap(() => this.navigateToRoute('/admin-location/location-review-targets'))
      ),
    { dispatch: false }
  );

  navigateReviewTargetsForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateReviewTargetsForm),
        tap(() => this.navigateToRoute('/review-targets/review-target-form'))
      ),
    { dispatch: false }
  );

  navigateResellers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateResellers),
        tap(() => this.navigateToRoute('/admin/reseller'))
      ),
    { dispatch: false }
  );

  navigateResellerProfile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateResellerProfile),
        tap(() => this.navigateToRoute('/resellers/reseller-profile'))
      ),
    { dispatch: false }
  );

  navigateReports$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateReports),
        tap(() => this.navigateToRoute('/reports'))
      ),
    { dispatch: false }
  );

  navigateReviews$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateReviews),
        tap(() => this.navigateToRoute('/reviews'))
      ),
    { dispatch: false }
  );

  navigateFeedback$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateFeedback),
        tap(() => this.navigateToRoute('/feedback'))
      ),
    { dispatch: false }
  );

  navigateCampaigns$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateCampaigns),
        tap(() => this.navigateToRoute('/campaigns'))
      ),
    { dispatch: false }
  );

  navigateTemplates$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateTemplates),
        tap(() => this.navigateToRoute('/templates'))
      ),
    { dispatch: false }
  );

  navigateWidgets$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateWidgets),
        tap(() => this.navigateToRoute('/widgets'))
      ),
    { dispatch: false }
  );

  navigateSharing$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateSharing),
        tap(() => this.navigateToRoute('/admin-location/sharing'))
      ),
    { dispatch: false }
  );

  navigateTo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.navigateTo),
        tap(({ pathUrl, queryParams }) => {
          this.navigateToRoute(pathUrl, queryParams || {})
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private store: Store<any>,
  ) { }
}
